import { rgbaHexToTuple, rgbaTupleToHex } from "./color";
import {
  RGBA_CHANNELS,
  MAX_SKIN_SIZE,
  SKIN_SIZE,
  type RgbaHex,
  type SkinDocument,
  type SkinModel,
  type SkinSize,
} from "./types";

export const TRANSPARENT: RgbaHex = "#00000000";

export function createSkinDocument(
  pixels: Uint8ClampedArray,
  model: SkinModel = "classic",
  revision = 0,
): SkinDocument {
  const size = inferSkinSize(pixels);
  if (!size) {
    throw new RangeError(
      `皮肤像素必须是 ${SKIN_SIZE}×${SKIN_SIZE} 或 ${MAX_SKIN_SIZE}×${MAX_SKIN_SIZE} RGBA 数据，收到 ${pixels.length} 个通道`,
    );
  }
  if (!Number.isSafeInteger(revision) || revision < 0) {
    throw new RangeError(`修订号必须是非负安全整数，收到：${revision}`);
  }
  return { pixels: new Uint8ClampedArray(pixels), model, size, revision };
}

export function createBlankSkin(
  model: SkinModel = "classic",
  size: SkinSize = SKIN_SIZE,
): SkinDocument {
  return createSkinDocument(
    new Uint8ClampedArray(size * size * RGBA_CHANNELS),
    model,
  );
}

export function cloneSkinDocument(document: SkinDocument): SkinDocument {
  return createSkinDocument(document.pixels, document.model, document.revision);
}

export function pixelOffset(
  x: number,
  y: number,
  size: SkinSize = SKIN_SIZE,
): number {
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    x < 0 ||
    y < 0 ||
    x >= size ||
    y >= size
  ) {
    throw new RangeError(`像素坐标超出 ${size}×${size} 皮肤范围：(${x}, ${y})`);
  }
  return (y * size + x) * RGBA_CHANNELS;
}

export function getPixel(
  document: SkinDocument,
  x: number,
  y: number,
): RgbaHex {
  const offset = pixelOffset(x, y, document.size);
  return rgbaTupleToHex(
    document.pixels[offset],
    document.pixels[offset + 1],
    document.pixels[offset + 2],
    document.pixels[offset + 3],
  );
}

export function setPixel(
  pixels: Uint8ClampedArray,
  x: number,
  y: number,
  color: RgbaHex,
): void {
  const size = inferSkinSize(pixels);
  if (!size)
    throw new RangeError(`无法从 ${pixels.length} 个 RGBA 通道推断皮肤尺寸`);
  const offset = pixelOffset(x, y, size);
  const tuple = rgbaHexToTuple(color);
  pixels[offset] = tuple[0];
  pixels[offset + 1] = tuple[1];
  pixels[offset + 2] = tuple[2];
  pixels[offset + 3] = tuple[3];
}

export function documentsEqual(
  left: SkinDocument,
  right: SkinDocument,
): boolean {
  if (
    left.model !== right.model ||
    left.size !== right.size ||
    left.pixels.length !== right.pixels.length
  )
    return false;
  return left.pixels.every((channel, index) => channel === right.pixels[index]);
}

export function inferSkinSize(
  pixels: Pick<Uint8ClampedArray, "length">,
): SkinSize | null {
  if (pixels.length === SKIN_SIZE * SKIN_SIZE * RGBA_CHANNELS) return SKIN_SIZE;
  if (pixels.length === MAX_SKIN_SIZE * MAX_SKIN_SIZE * RGBA_CHANNELS)
    return MAX_SKIN_SIZE;
  return null;
}
